import { useMutation }                                            from '@apollo/client'
import { TrashIcon }                                              from '@heroicons/react/20/solid'
import { ArrowUturnLeftIcon, XCircleIcon }                        from '@heroicons/react/24/solid'
import { UploadCloud }                                            from 'lucide-react'
import { useEffect, useRef, useState }                            from 'react'
import Dropzone                                                   from 'react-dropzone'
import { useDispatch }                                            from 'react-redux'
import { useNavigate }                                            from 'react-router-dom'
import { setShopSettingsProductsRefetch, setShopProductsRefetch } from '../../../features/shop/shopSlice'
import { setToasts, showToaster }                                 from '../../../features/toaster/toasterSlice'

import {
  CREATE_SHOP_PRODUCT,
  CREATE_SHOP_PRODUCT_IMAGE,
  DELETE_SHOP_PRODUCT_IMAGE,
  DELETE_SHOP_PRODUCT_VARIANTS,
  UPDATE_SHOP_PRODUCT,
  UPDATE_SHOP_PRODUCT_VARIANTS
} from '../../../graphql/shop'

import { appCdnUrl, assetFilePath, uploadFile } from '../../../utilities'
import { Editor }                               from '../../Editor'
import { Image }                                from '../../Image'
import { Button, TextField }                    from '../../index'
import { LoadingEllipsis }                      from '../../Loaders'
import { SelectOption }                         from '../../Select/Select'
import { Switch }                               from '../../Switch'
import SettingsShopProductFormVariationsTable   from './SettingsShopProductFormVariationsTable'

export default function SettingsShopProductForm( props: any ): JSX.Element {
  const { shopProduct, currentTenant, sizeGuides, deliveryOptions, categories, refetchProduct } = props
  const dispatch: any                                                                           = useDispatch()
  const navigate: any                                                                           = useNavigate()
  const [ name, setName ]                                                                       = useState( shopProduct?.name ?? '' )
  const [ categoryId, setCategoryId ]                                                           = useState( shopProduct?.category_id ?? '' )
  const [ deliveryId, setDeliveryId ]                                                           = useState( shopProduct?.delivery_option_id ?? '' )
  const [ sizeGuideId, setSizeGuideId ]                                                         = useState( shopProduct?.size_guide_id ?? '' )
  const [ description, setDescription ]                                                         = useState( shopProduct?.description ?? '' )
  const [ price, setPrice ]                                                                     = useState( `${ shopProduct?.price ?? 0 }` )
  const [ vat, setVat ]                                                                         = useState( shopProduct?.vat ? `${ shopProduct.vat }` : '' )
  const [ published, setPublished ]                                                             = useState( shopProduct?.published ?? false )
  const [ productImages, setShopProductImages ]                                                 = useState( shopProduct?.images ?? [] )
  const [ variants, setVariants ]                                                               = useState( shopProduct?.variants ?? [] )
  const [ configVariants, setConfigVariants ]                                                   = useState( shopProduct?.config?.variants ?? [] )
  const [ quantityField, setQuantityField ]                                                     = useState( shopProduct?.config?.quantityField ?? '' )
  const [ variantLabel, setVariantLabel ]                                                       = useState( shopProduct?.config?.variantLabel ?? '' )
  const [ singleItem, setSingleItem ]                                                           = useState( shopProduct?.config?.singleItem ?? false )
  const [ isFree, setIsFree ]                                                                   = useState( shopProduct?.config?.isFree ?? false )
  const [ showVat, setShowVat ]                                                                 = useState( shopProduct?.config?.showVat ?? false )
  const [ extraDelivery, setExtraDelivery ]                                                     = useState( shopProduct?.config?.extraDelivery ?? false )
  const [ hasFreeItems, setHasFreeItems ]                                                       = useState( shopProduct?.config?.hasFreeItemsPerLocation ?? false )
  const [ isColourVariation, setIsColourVariation ]                                             = useState( shopProduct?.config?.variants?.includes( 'colour' ) ?? false )
  const [ isSizeVariation, setIsSizeVariation ]                                                 = useState( shopProduct?.config?.variants?.includes( 'size' ) ?? false )
  const [ freeItemsQty, setFreeItemsQty ]                                                       = useState( `${ shopProduct?.config?.freeItemsPerLocationQty ?? 0 }` )
  const [ nameError, setNameError ]                                                             = useState( false )
  const [ categoryIdError, setCategoryIdError ]                                                 = useState( false )
  const [ quantityFieldError, setQuantityFieldError ]                                           = useState( false )
  const [ freeItemsQtyError ]                                                                   = useState( false )
  const [ deliveryIdError, setDeliveryIdError ]                                                 = useState( false )
  const [ descriptionError, setDescriptionError ]                                               = useState( false )
  const [ submitting, setSubmitting ]                                                           = useState( false )
  const [ imagesToUpload, setImagesToUpload ]                                                   = useState( [] )
  const [ variantsToDelete, setVariantsToDelete ]                                               = useState( [] )
  const filePreviewContainer: any                                                               = useRef( null )

  const [ deleteProductImage, { loading: deletingImage } ] = useMutation( DELETE_SHOP_PRODUCT_IMAGE, {
    onCompleted: async ( { deleteShopProductImage }: any ): Promise<any> => {
      if ( deleteShopProductImage?.__typename === 'FieldError' ) {
        dispatchMessage( 'error', 'Image not deleted. Server error.' )
      } else {
        dispatchMessage( 'success', 'Image deleted successfully' )
        await refetchProduct()
      }
    },
    onError:     (): void => dispatchMessage( 'error', 'Image not deleted. Please try again' )
  } )

  const [ createProductImage ] = useMutation( CREATE_SHOP_PRODUCT_IMAGE, {
    onCompleted: async (): Promise<void> => {
      if ( shopProduct ) await refetchProduct()
    },
    onError:     (): void => dispatchMessage( 'error', 'Image NOT uploaded!' )
  } )

  const [ createProduct, { loading: creating } ] = useMutation( CREATE_SHOP_PRODUCT, {
    onCompleted: async (): Promise<void> => {
      dispatchMessage( 'success', 'Shop product created!' )
      await dispatch( setShopProductsRefetch( true ) )
      await dispatch( setShopSettingsProductsRefetch( true ) )
      navigate( '/settings/shop/products' )
    },
    onError:     (): void => dispatchMessage( 'error', 'Creating failed. Please try again' )
  } )

  const [ updateProduct, { loading: updating } ] = useMutation( UPDATE_SHOP_PRODUCT, {
    onCompleted: async (): Promise<void> => {
      await refetchProduct()
      await dispatch( setShopProductsRefetch( true ) )
      await dispatch( setShopSettingsProductsRefetch( true ) )
      dispatchMessage( 'success', 'Shop product updated!' )
    },
    onError:     (): void => dispatchMessage( 'error', 'Update failed. Please try again' )
  } )

  const [ updateProductVariants ] = useMutation( UPDATE_SHOP_PRODUCT_VARIANTS, {
    onError: (): void => dispatchMessage( 'error', 'Update Variants failed. Please try again' )
  } )

  const [ deleteProductVariants ] = useMutation( DELETE_SHOP_PRODUCT_VARIANTS, {
    onError: (): void => dispatchMessage( 'error', 'Delete Variants failed. Please try again' )
  } )

  const dispatchMessage: any = ( type: string, message: string ): void => {
    dispatch( setToasts( [ { id: '1', type, message } ] ) )
    dispatch( showToaster( true ) )
  }

  const resetErrors: any = (): void => {
    setNameError( false )
    setCategoryIdError( false )
    setQuantityFieldError( false )
    setDeliveryIdError( false )
    setDescriptionError( false )
  }

  const checkErrors: any = (): boolean => {
    resetErrors()

    let hasErrors: boolean = false

    if ( !name.length || name.length < 3 ) {
      setNameError( true )
      hasErrors = true
    }

    if ( !description.replace( /(<([^>]+)>)/gi, '' ).length ) {
      setDescriptionError( true )
      hasErrors = true
    }

    if ( !quantityField.length ) {
      setQuantityFieldError( true )
      hasErrors = true
    }

    if ( !categoryId.length ) {
      setCategoryIdError( true )
      hasErrors = true
    }

    if ( !deliveryId.length ) {
      setDeliveryIdError( true )
      hasErrors = true
    }

    return hasErrors
  }

  const handleSetFiles: any = ( images: any ): void => {
    const initialFilesQuantity: number = images.length
    const loadableFiles: any           = imagesToUpload.filter( ( f: any ): any => !f.deleted )
    images                             = images.filter( ( f: any ): any => f.size < 10485760 )

    if ( images.length !== initialFilesQuantity ) {
      alert( 'Max file size is 10MB. Some of images has been removed.' )
    }

    if ( ( loadableFiles.length >= 5 ) || ( productImages.length >= 5 ) ) {
      return alert( 'Limit of 5 images reached' )
    }

    if ( productImages.length > 0 ) {
      if ( ( loadableFiles.length + images.length ) > ( 5 - shopProduct.images.length ) ) {
        images.splice( Math.max( 0, ( 5 - shopProduct.images.length ) ) )
      }
    } else {
      if ( ( loadableFiles.length + images.length ) > 5 ) {
        images.splice( Math.max( 0, 5 - loadableFiles.length ) )
      }
    }

    setImagesToUpload( [ ...imagesToUpload, ...images ] )
  }

  const handleRemoveFileToUpload: any = ( index: number ): void => {
    setImagesToUpload( [ ...imagesToUpload.map( ( f: any, i: number ): any => {
      if ( index === i ) f.deleted = true
      return f
    } ) ] )
  }

  const handleDeleteImage: any = async ( image: any ): Promise<void> => {
    if ( !image?.id || deletingImage ) return

    if ( window.confirm( 'Are you sure you want to delete this image?' ) ) {
      await deleteProductImage( { variables: { input: { id: image.id } } } )
    }
  }

  const uploadFiles: any = async ( productId: string ): Promise<void> => {
    if ( !imagesToUpload.length ) return

    try {
      for await ( const file of imagesToUpload ) {
        if ( !file.deleted ) {
          const newFileUrl: string = await uploadFile( file, ( currentTenant.uuid + '/products/' + productId ), 'true' )

          if ( newFileUrl ) {
            const filename: any      = file.name.replace( /\.[^/.]+$/, '' )
            const fileTypeArray: any = file.type.split( '/' )

            await createProductImage( {
                                        variables: {
                                          input: {
                                            'product_id': productId,
                                            'type':       fileTypeArray[ 0 ],
                                            'file_ext':   fileTypeArray[ fileTypeArray.length - 1 ],
                                            'file_type':  file.type,
                                            'name':       filename.length ? filename : 'no_name',
                                            'url':        newFileUrl
                                          }
                                        }
                                      } )
          }
        }
      }
    } catch ( e ) {
      dispatchMessage( 'error', 'Upload failed' )
    }
  }

  const previewFile: any = ( file: any, index: number ): any => {
    if ( filePreviewContainer.current && file.type.startsWith( 'image/' ) ) {
      const preview: any = filePreviewContainer.current.querySelector( `.image-preview-${ index }` )
      const reader: any  = new FileReader()

      if ( preview ) {
        reader.addEventListener( 'load', (): any => preview.src = reader.result, false )
        reader.readAsDataURL( file )
      }
    }
  }

  const handleVariantChange: any = ( action: string, data: any, tIndex: number ): any => {
    let currentVariants: any = [ ...variants ]

    switch ( action ) {
      case 'add':
        data.product_id = shopProduct.id
        currentVariants = [ ...currentVariants, data ]

        break
      case 'remove':
        if ( data.id !== 'new' ) {
          setVariantsToDelete( [ ...variantsToDelete, data.id ] )
        }

        currentVariants.splice( tIndex, 1 )

        break
      case 'update':
        let variantUpdate: any = { ...currentVariants[ tIndex ] }

        variantUpdate[ Object.keys( data )[ 0 ] ] = Object.values( data )[ 0 ]
        currentVariants[ tIndex ]                 = { ...variantUpdate }

        break
      case 'updateConfig':
        let variantUpdateConfig: any = { ...currentVariants[ tIndex ] }
        let variantConfig: any       = {
          ...( typeof variantUpdateConfig.config === 'string'
               ? JSON.parse( variantUpdateConfig.config )
               : variantUpdateConfig.config )
        }

        if ( Object.keys( variantConfig ).length > 0 ) {
          variantConfig[ Object.keys( data )[ 0 ] ] = Object.values( data )[ 0 ]
        } else {
          variantConfig = { [ Object.keys( data )[ 0 ] ]: Object.values( data )[ 0 ] }
        }

        variantUpdateConfig.config = { ...variantConfig }
        currentVariants[ tIndex ]  = { ...variantUpdateConfig }

        break
      default:
        break
    }

    setVariants( [ ...currentVariants ] )
  }

  const updateProductConfigVariants: any = ( variant: string, value: boolean ): any => {
    let updatedVariants: any[] = [ ...configVariants ]
    const variantIndex: any    = updatedVariants.indexOf( variant )

    if ( variantIndex === -1 ) {
      updatedVariants.push( variant )
    } else {
      updatedVariants = updatedVariants.filter( ( v: any ): any => v !== variant )
    }

    setConfigVariants( updatedVariants )

    if ( variant === 'colour' ) {
      setIsColourVariation( value )
    }

    if ( variant === 'size' ) {
      setIsSizeVariation( value )
    }
  }

  const handleSubmit: any = async (): Promise<any> => {
    if ( checkErrors() || submitting || creating || updating ) {
      dispatchMessage( 'error', 'Please check form errors before submitting!' )
      return
    }

    setSubmitting( true )

    const formData: any = {
      published,
      name,
      category_id:        categoryId,
      delivery_option_id: deliveryId,
      size_guide_id:      sizeGuideId?.length ? sizeGuideId : null,
      description,
      price,
      vat:                !isNaN( parseInt( vat ) ) ? vat : '0',
      config:             {
        isFree,
        showVat,
        singleItem,
        extraDelivery,
        quantityField,
        variantLabel,
        hasFreeItemsPerLocation: hasFreeItems,
        freeItemsPerLocationQty: freeItemsQty,
        variants:                configVariants
      }
    }

    let productId: string | null = shopProduct ? shopProduct.id : null

    if ( shopProduct ) {
      formData.id = productId
      await updateProduct( { variables: { input: formData } } )
    } else {
      const { data } = await createProduct( { variables: { input: formData } } )

      if ( data?.createShopProduct?.data?.id ) {
        productId = data.createShopProduct.data.id
      }
    }

    setTimeout( async (): Promise<void> => {
      if ( productId ) {
        if ( variantsToDelete.length ) {
          await deleteProductVariants( { variables: { input: { variants: variantsToDelete } } } )
          setVariantsToDelete( [] )
        }

        await uploadFiles( productId )

        await updateProductVariants( { variables: { input: { variants } } } )
        setImagesToUpload( [] )
      }

      setSubmitting( false )
    }, 500 )
  }

  useEffect( (): any => {
    let shouldFetch: boolean = true

    if ( shouldFetch ) {
      setShopProductImages( shopProduct?.images ?? [] )
    }

    return (): void => {
      shouldFetch = false
    }
  }, [ shopProduct ] )

  return (
    <>
      <div className="w-24 my-4">
        <Button
          label="Back"
          blue
          onClick={ (): any => navigate( -1 ) }
          icon={ <ArrowUturnLeftIcon className="w-4 h-4" /> }
        />
      </div>

      <div className="flex flex-col items-center w-full bg-white rounded-3xl p-4 xl:p-5">
        { ( creating || updating ) && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4" /> }

        { !creating && !updating &&
          <>
            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Published</div>
              <div className="w-2/3">
                <Switch
                  checked={ published }
                  onChange={ setPublished }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Name</div>
              <div className="w-2/3">
                <TextField
                  label=""
                  value={ name }
                  name="title"
                  type="text"
                  onChange={ setName }
                  errorField={ nameError ? 'name' : '' }
                  isRequired
                />
                </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Category</div>
              <div className="w-2/3">
                <SelectOption
                  required
                  selected={ categoryId }
                  setSelected={ setCategoryId }
                  placeholder="Select Category"
                  wrapperClasses={ `text-grey-input rounded-2xl overflow-hidden border ${ categoryIdError ? 'border-red-600' : 'border-grey-light' }` }
                  fieldClasses="border-none focus:ring-0 w-full"
                  options={ categories?.map( ( cat: any ): any => {
                    return { key: cat.id, value: cat.name }
                  } ) }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Price</div>
              <div className="w-2/3">
                <TextField
                  label=""
                  value={ price }
                  name="price"
                  type="number"
                  onChange={ setPrice }
                  step={ 0.01 }
                  placeholder="Price"
                  wrapperClasses="relative z-0 w-full group text-sm"
                  poundMarkClasses="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-900"
                  fieldClasses="block pb-1.5 pl-2.5 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                  poundMark
                  isRequired
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Vat</div>
              <div className="w-2/3">
                <TextField
                  label=""
                  value={ vat }
                  name="vat"
                  type="number"
                  onChange={ setVat }
                  step={ 1 }
                  placeholder="Vat"
                  percentMark
                  isRequired
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Show price with VAT</div>
              <div className="w-2/3">
                <Switch
                  checked={ showVat }
                  onChange={ setShowVat }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Quantity Field</div>
              <div className="w-2/3">
                <TextField
                  label=""
                  value={ quantityField }
                  name="quantity_field"
                  type="text"
                  onChange={ setQuantityField }
                  errorField={ quantityFieldError ? 'quantity_field' : '' }
                  isRequired
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Variant Label</div>
              <div className="w-2/3">
                <TextField
                  label=""
                  value={ variantLabel }
                  name="variant_label_field"
                  type="text"
                  onChange={ setVariantLabel }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Delivery</div>
              <div className="w-2/3">
                <SelectOption
                  required
                  selected={ deliveryId }
                  setSelected={ setDeliveryId }
                  placeholder="Select Delivery"
                  wrapperClasses={ `text-grey-input rounded-2xl overflow-hidden border ${ deliveryIdError ? 'border-red-600' : 'border-grey-light' }` }
                  fieldClasses="border-none focus:ring-0 w-full"
                  options={ deliveryOptions?.map( ( item: any ): any => {
                    return { key: item.id, value: `${ item.name } - £${ item.amount }` }
                  } ) }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Contains free items</div>
              <div className="w-2/3">
                <Switch
                  disabled={ isFree }
                  checked={ hasFreeItems }
                  onChange={ setHasFreeItems }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Item is free</div>
              <div className="w-2/3">
                <Switch
                  disabled={ hasFreeItems }
                  checked={ isFree }
                  onChange={ setIsFree }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Free quantity</div>
              <div className="w-2/3">
                <TextField
                  label=""
                  value={ freeItemsQty }
                  name="free_quantity"
                  type="number"
                  step={ 1 }
                  onChange={ setFreeItemsQty }
                  errorField={ freeItemsQtyError ? 'free_quantity' : '' }
                  isRequired
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Single Item</div>
              <div className="w-2/3">
                <Switch
                  disabled={ ( hasFreeItems || isFree ) }
                  checked={ singleItem }
                  onChange={ ( val: boolean ): void => {
                    if ( !val ) setExtraDelivery( false )
                    setSingleItem( val )
                  } }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Extra delivery</div>
              <div className="w-2/3">
                <Switch
                  disabled={ !singleItem }
                  checked={ extraDelivery }
                  onChange={ setExtraDelivery }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-1/3 font-medium text-blue-header">Description</div>
              <div className="w-2/3 flex flex-wrap items-center p-6 pt-2 bg-grey-light-secondary rounded-2xl w-full md:w-2/3 forum-lexical">
                <Editor
                  /* @ts-ignore */
                  initialHtml={ description }
                  errorField={ descriptionError }
                  actionNode={ ( content: any ): void => setDescription( content ) }
                  placeholder="Type Something..."
                  simpleToolbar
                  isRequired
                />
              </div>
            </div>

            <hr className="w-full pb-4" />

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-full font-medium text-blue-header mb-4">Size Guide</div>
              <div className="w-full flex flex-wrap">
                { sizeGuides?.map( ( guide: any, index: number ): any =>
                                     <div className="flex items-center w-full md:w-1/2 lg:w-1/3 xl:w-1/4 p-2" key={ index }>
                                       <input className="cursor-pointer" type="radio" checked={ ( guide.id === sizeGuideId ) } onChange={ (): any => setSizeGuideId( guide.id ) } />
                                       <img className="w-full object-contain h-full cursor-pointer" key={ index } alt={ guide.name } src={ appCdnUrl + guide.url } onClick={ (): any => setSizeGuideId( guide.id ) } />
                                     </div>
                ) }
              </div>
            </div>

            <hr className="w-full pb-4" />

            <div className="flex flex-wrap items-center w-full mb-6">
              <div className="w-full font-medium text-blue-header mb-4">Gallery</div>
              <div className="w-full">
                <div className="cursor-pointer no-select p-6 rounded-2xl bg-grey-light-secondary">
                  <Dropzone onDrop={ ( attachedFiles: any ): any => handleSetFiles( attachedFiles ) } multiple={ true }>
                    { ( { getRootProps, getInputProps }: any ): any => (
                      <section>
                        <div { ...getRootProps() }>
                          <input { ...getInputProps() } />

                          <div className="flex items-center justify-center flex-col space-y-2">
                            <UploadCloud className="w-12 h-12" />
                            <p className="text-blue-input text-sm font-medium">Drag 'n' drop or click to select file</p>
                          </div>
                        </div>
                      </section>
                    ) }
                  </Dropzone>
                </div>

                { ( imagesToUpload.length > 0 ) &&
                  <div ref={ filePreviewContainer } className="mt-4 pb-2 flex items-center flex-wrap gap-3">
                    { imagesToUpload.map( ( image: any, index: number ): any => {
                      return ( !image.deleted &&
                               <div className="flex items-center justify-center flex-col w-16 h-32 mr-2 relative rounded-md relative" key={ index }>
                                 <button
                                   title="Remove"
                                   className="absolute -right-1 -top-1 bg-white shadow rounded-full"
                                   onClick={ (): any => handleRemoveFileToUpload( index ) }
                                 >
                                   <XCircleIcon className="w-4 h-4" />
                                 </button>

                                 <Image
                                   klass={ `image-preview-${ index }` }
                                   src={ previewFile( image, index ) }
                                   defaultSrc={ assetFilePath( image.type.split( '/' ).pop() ) }
                                 />

                                 <small className="text-xs truncate w-16">{ image.name }</small>
                               </div> )
                    } ) }
                  </div>
                }

                { ( productImages.length > 0 ) &&
                  <div className="mt-6 grid grid-cols-4 lg:grid-cols-8 gap-2 lg:gap-4 w-full">
                    { productImages.map( ( image: any ): any =>
                                           <div className="relative flex items-center justify-center border w-full" key={ image.id }>
                                             <button
                                               title="Delete"
                                               className="absolute -right-2 -top-2 bg-red-500 text-white rounded-full"
                                               onClick={ (): any => handleDeleteImage( image ) }
                                               disabled={ deletingImage }
                                             >
                                               <TrashIcon className="w-6 h-6 p-1" />
                                             </button>

                                             <div className="flex flex-col">
                                               <div className="flex items-center justify-center w-16 h-16 lg:w-24 lg:h-24">
                                                 <Image
                                                   klass="max-w-16 max-h-16 lg:max-w-24 lg:max-h-24"
                                                   src={ appCdnUrl + image.url }
                                                   alt={ image.name ?? shopProduct.name }
                                                   defaultSrc={ assetFilePath( image.file_ext ?? 'jpg' ) }
                                                 />
                                               </div>
                                             </div>
                                           </div> ) }
                  </div>
                }
              </div>
            </div>

            <hr className="w-full pb-4" />

            { shopProduct
              ? <div className="flex flex-wrap items-center w-full mb-6">
                  <div className="w-full font-medium text-blue-header">Variants</div>
                    <p className="text-sm text-gray-600 mb-4">Please select variants that are related to the product.</p>

                    <div className="flex space-x-4 w-full mb-4">
                      <div className="flex items-center justify-center">
                        <div className="mr-2">Colour</div>
                        <Switch
                          wrapperClasses="flex items-center justify-center"
                          checked={ isColourVariation }
                          onChange={ ( value: boolean ): any => updateProductConfigVariants( 'colour', value ) }
                        />
                      </div>

                      <div className="flex items-center justify-center">
                        <div className="mr-2">Size</div>
                        <Switch
                          wrapperClasses="flex items-center justify-center"
                          checked={ isSizeVariation }
                          onChange={ ( value: boolean ): any => updateProductConfigVariants( 'size', value ) }
                        />
                      </div>
                    </div>

                    <SettingsShopProductFormVariationsTable
                      isColourVariation={ isColourVariation }
                      isSizeVariation={ isSizeVariation }
                      variants={ variants }
                      images={ productImages }
                      handleVariantChange={ handleVariantChange }
                    />
                </div>
              : <p>Create product to enable variants section</p>
            }

            <div className="flex flex-wrap w-full">
              <div className="flex pt-5">
                <Button label={ shopProduct ? 'Update product' : 'Add new product' } yellow onClick={ handleSubmit } />
              </div>
            </div>
          </>
        }
      </div>
    </>
  )
}
